import { useState } from 'react'

import { Box, RadioButton, Spinner, Text } from '@factorialco/deprecated-design-system'

import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import i18n from 'modules/core/lib/i18n'

import { ActionPreviewHeader } from 'modules/processes/components/ItemWizard/Actions/Steps/Preview/Header'
import { usePreviewProvider } from 'modules/processes/components/ItemWizard/Providers/preview'
import questionConfigs from 'modules/surveys/store/collections/questionConfigs'

interface FieldProps {
  field_type: string
  choice_options?: {
    id: number
    label: string
    value: string
    is_active: boolean
  }[]
  min_value?: number
  max_value?: number
}

export const Preview = () => {
  const { previewValues } = usePreviewProvider()
  const [loading, setLoading] = useState<boolean>(false)

  useUnsafeEffect(() => {
    if (previewValues.survey) {
      setLoading(true)

      questionConfigs
        .fetch({
          data: {
            surveys_form_id: previewValues.survey.id,
          },
        })
        .then(() => {
          setLoading(false)
        })
    }
  }, [previewValues])

  const FieldTypeText = ({ question }: { question: FieldProps }) => {
    return (
      <Box
        background="grey000White"
        padding="s64"
        borderRadius={{ all: 'abs008' }}
        border={{ all: { width: 's1', color: 'grey400', style: 'solid' } }}
      />
    )
  }

  const FieldTypeNumber = ({ question }: { question: FieldProps }) => {
    return (
      <Box
        background="grey000White"
        padding="s24"
        borderRadius={{ all: 'abs008' }}
        border={{ all: { width: 's1', color: 'grey400', style: 'solid' } }}
      />
    )
  }

  const FieldTypeChoice = ({ question }: { question: FieldProps }) => {
    const activeOptions = question.choice_options?.filter((option) => option.is_active)

    return (
      <Box
        borderRadius={{ all: 'abs008' }}
        border={{ all: { style: 'solid', color: 'grey400', width: 's1' } }}
      >
        {activeOptions?.map((option, index) => {
          return (
            <Box
              key={option.id}
              minHeight="s64"
              flexDirection="row"
              alignItems="center"
              gap="s12"
              padding="s16"
              {...(index !== 0 && {
                border: { top: { style: 'solid', color: 'grey400', width: 's1' } },
              })}
            >
              <RadioButton
                value={''}
                checked={false}
                onChange={() => {
                  return 1
                }}
              />
              <Text.Normal size="300">{option.label}</Text.Normal>
            </Box>
          )
        })}
      </Box>
    )
  }

  const FieldTypeRating = ({ question }: { question: FieldProps }) => {
    const { min_value, max_value } = question
    const questionValues = Array.from(
      { length: Number(max_value) - Number(min_value) + 1 },
      (_, index) => index + Number(min_value)
    )

    return (
      <Box
        borderRadius={{ all: 'abs008' }}
        border={{ all: { style: 'solid', color: 'grey400', width: 's1' } }}
        flexDirection="row"
        justifyContent="spaceBetween"
        alignItems="center"
      >
        {questionValues.map((value) => {
          return (
            <Box
              flexDirection="row"
              justifyContent="center"
              alignItems="center"
              paddingY="s12"
              key={value}
              flexGrow
              {...(value !== max_value && {
                border: { right: { style: 'solid', color: 'grey400', width: 's1' } },
              })}
            >
              {value}
            </Box>
          )
        })}
      </Box>
    )
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const Field = ({ question }: { question: FieldProps }) => {
    switch (question.field_type) {
      case 'single_choice':
        return <FieldTypeChoice question={question} />
      case 'cents':
        return <FieldTypeNumber question={question} />
      case 'rating':
        return <FieldTypeRating question={question} />
      default:
        return <FieldTypeText question={question} />
    }
  }

  const activeQuestionConfigs = questionConfigs.sortByPosition.filter((x) => !x.attributes.archived)
  return (
    <>
      <ActionPreviewHeader
        description={i18n.t('processes.process_settings.actions.survey_action.preview_description')}
      />

      <Box paddingTop="s24" paddingRight="s12">
        <Box
          padding="s24"
          gap="s8"
          borderRadius={{ all: 'abs016' }}
          border={{ all: { width: 's1', color: 'grey400', style: 'solid' } }}
        >
          {loading && <Spinner />}
          {!loading && (
            <Box gap="s24">
              <Text.Header2 color="grey1000" weight="medium">
                {previewValues.survey.attributes.name}
              </Text.Header2>
              {previewValues.survey.attributes.description && (
                <Text.Normal size="300">{previewValues.survey.attributes.description}</Text.Normal>
              )}
              <Box gap="s16">
                {activeQuestionConfigs.map((questionConfig, index) => {
                  return (
                    <>
                      <Box key={index} flexDirection="row" paddingTop="s12">
                        <Box
                          background="viridian200"
                          borderRadius={{ all: 'abs008' }}
                          paddingY="s4"
                          paddingX="s4"
                          minHeight="s24"
                          maxHeight="s32"
                          minWidth="s32"
                          alignItems="center"
                        >
                          <Text.Normal size="300" color="viridian1200" weight="medium">
                            {index + 1}
                          </Text.Normal>
                        </Box>
                        <Box paddingLeft="s16" gap="s8">
                          <Text.Normal size="300" weight="medium" color="grey1000">
                            {questionConfig.attributes.custom_field.label}
                          </Text.Normal>
                          <Text.Normal size="300" weight="normal" color="grey1000">
                            {questionConfig.attributes.custom_field.description}
                          </Text.Normal>
                        </Box>
                      </Box>
                      <Field question={questionConfig.attributes.custom_field} />
                    </>
                  )
                })}
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </>
  )
}
